import {
  cannectDefaultProduct,
  cannectDefaultProductCapsule,
  cannectDefaultProductCream,
  cannectDefaultProductGummy,
  cannectDefaultProductNoTarg,
  cannectDefaultProductOil,
  aspaecDefaultProduct
} from '@cannect/assets/img'
import useMediaQuery from '@cannect/hooks/useMediaQuery'
import useSignedUser from '@cannect/hooks/useSignedUser'
import { cn } from '@cannect/lib/utils'
import { Button, Drawer, Dropdown, ProgressBar, Separator, Typography } from '@cannect/new-components/atoms'
import { buttonVariants } from '@cannect/new-components/atoms/Button'
import { ModalConfirm } from '@cannect/new-components/molecules'
import { TModalConfirmMethods } from '@cannect/new-components/molecules/ModalConfirm/types'
import { formatToReal } from '@cannect/utils/number'
import { AnimatePresence, motion } from 'framer-motion'
import { HTMLAttributes, ImgHTMLAttributes, forwardRef, useMemo, useRef } from 'react'
import { LuTrash2 } from 'react-icons/lu'
import { MdStars } from 'react-icons/md'
import { TFlavorSelect, TFreeShippingProgress, THeaderImage, THeaderInfo, TPrice, TRemoveButton } from './types'
import { Tooltip } from 'antd'

import GroupImage from '../../../assets/img/curatorship/Camada_1.png'
// import GroupImageDisable from '../../../assets/svg/product-details/Group-.svg'

import GroupImageGMP from '../../../assets/img/curatorship/Camada_2.png'
// import GroupImageGMPDisable from '../../../assets/svg/product-details/Group-1.svg'

import GroupImageEUGMP from '../../../assets/svg/product-details/eu-gmp-seal.svg'
// import GroupImageEUGMPDisable from '../../../assets/svg/product-details/Group-2.svg'

import GroupImageVegan from '../../../assets/svg/product-details/Group (7).svg'
// import GroupImageVeganDisable from '../../../assets/svg/product-details/Group (8).svg'

import GroupImageOrganic from '../../../assets/svg/product-details/Group (6).svg'
// import GroupImageOrganicDisable from '../../../assets/svg/product-details/Group-6.svg'

import GroupImageGACP from '../../../assets/svg/product-details/Group (3).svg'
// import GroupImageGACPDisable from '../../../assets/svg/product-details/Group-3.svg'

import GroupImageSwissGMP from '../../../assets/svg/product-details/swiss-gmp.svg'
// import GroupImageSwissGMPDisable from '../../../assets/svg/product-details/swiss-gmp-disabled.svg'

const Root = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('relative flex w-full gap-4 sm:gap-6', className)} {...props} />
))
Root.displayName = 'Product.Root'

const Header = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('grid grid-cols-[auto,1fr] gap-x-2 gap-y-1 sm:gap-x-3', className)} {...props} />
))
Header.displayName = 'Product.Header'

const HeaderImage = forwardRef<HTMLImageElement, THeaderImage & ImgHTMLAttributes<HTMLImageElement>>(
  (
    { className, isNationalProduct, isSupplementProduct, shapeName, src, alt = 'product-img', product, deliveryByAssociation, ...props },
    ref
  ) => {
    const { signedUser } = useSignedUser()

    const isPatientUser = useMemo(() => signedUser?.registration_type === 1, [signedUser])

    const defaultImage = isNationalProduct ? cannectDefaultProduct : cannectDefaultProductNoTarg
    const isPillProduct = product?.origin === 'pill'
    let imageSource = ''

    if (isPillProduct) {
      imageSource = src || defaultImage
    } else if (deliveryByAssociation) {
      imageSource = aspaecDefaultProduct
    } else {
      const shapeNameFormatted = shapeName?.toLowerCase().replace(' ', '-')

      switch (shapeNameFormatted) {
        case 'óleo':
          imageSource = isPatientUser && !isSupplementProduct ? cannectDefaultProductOil : src || cannectDefaultProductOil
          break
        case 'cápsula':
          imageSource =
            isPatientUser && !isSupplementProduct ? cannectDefaultProductCapsule : src || cannectDefaultProductCapsule
          break
        case 'creme':
          imageSource =
            isPatientUser && !isSupplementProduct ? cannectDefaultProductCream : src || cannectDefaultProductCream
          break
        case 'goma':
          imageSource =
            isPatientUser && !isSupplementProduct ? cannectDefaultProductGummy : src || cannectDefaultProductGummy
          break
        default:
          imageSource = isPatientUser && !isSupplementProduct ? defaultImage : src || defaultImage
          break
      }
    }

    return (
      <img
        src={imageSource}
        alt={alt}
        ref={ref}
        className={cn(
          'row-span-2 h-16 w-16 rounded-md object-contain shadow-md sm:h-20 sm:w-20 md:h-24 md:w-24',
          className
        )}
        {...props}
      />
    )
  }
)

const CurationBadges = forwardRef<HTMLDivElement, { product?: any; isPrescriber?: boolean } & HTMLAttributes<HTMLDivElement>>(
  ({ product, isPrescriber, className, ...props }, ref) => {
    if (!isPrescriber || !product?.curatorship) return null

    return (
      <div ref={ref} className={cn('flex flex-row flex-wrap gap-1 sm:gap-2', className)} {...props}>
        {product?.curatorship?.Adicionais?.Vegano && (
          <Tooltip title="Garante que o produto não contém ingredientes de origem animal.">
            <img src={GroupImageVegan} alt="Selo Vegano" className="h-auto w-14 sm:w-16 md:w-20 lg:w-18" />
          </Tooltip>
        )}
        {product?.curatorship?.Adicionais['Orgânico'] && (
          <Tooltip title="Certifica que o cultivo seguiu práticas orgânicas sustentáveis.">
            <img src={GroupImageOrganic} alt="Selo Orgânico" className="h-auto w-14 sm:w-16 md:w-20 lg:w-18" />
          </Tooltip>
        )}
        {product?.curatorship['Certificados fornecidos']?.GACP && (
          <Tooltip title="Assegura boas práticas agrícolas e de colheita para plantas medicinais.">
            <img src={GroupImageGACP} alt="Selo GACP" className="h-auto w-14 sm:w-16 md:w-20 lg:w-18" />
          </Tooltip>
        )}
        {product?.curatorship['Certificados fornecidos']['Grau farmacêutico x nutricional'] ===
          'Farmacêutico (cGMP)' && (
          <Tooltip title="Certifica conformidade com normas farmacêuticas internacionais.">
            <img src={GroupImage} alt="Selo cGMP Farmacêutico" className="h-auto w-14 sm:w-16 md:w-20 lg:w-18" />
          </Tooltip>
        )}
        {product?.curatorship['Certificados fornecidos']['Grau farmacêutico x nutricional'] ===
          'Farmacêutico (EU-GMP)' && (
          <Tooltip title="Segue as exigências da União Europeia para produtos farmacêuticos.">
            <img src={GroupImageEUGMP} alt="Selo EU-GMP" className="h-auto w-14 sm:w-16 md:w-20 lg:w-18" />
          </Tooltip>
        )}
        {product?.curatorship['Certificados fornecidos']['Grau farmacêutico x nutricional'] ===
          'Nutricional (cGMP)' && (
          <Tooltip title="Garante boas práticas de fabricação para produtos nutricionais.">
            <img src={GroupImageGMP} alt="Selo cGMP Nutricional" className="h-auto w-14 sm:w-16 md:w-20 lg:w-18" />
          </Tooltip>
        )}
        {product?.curatorship['Certificados fornecidos']['Grau farmacêutico x nutricional'] ===
          'Farmacêutico (GMP Suíço)' && (
          <Tooltip title="Cumpre os requisitos suíços de Boas Práticas de Fabricação para produtos farmacêuticos.">
            <img src={GroupImageSwissGMP} alt="Selo GMP Suíço" className="h-auto w-16 sm:w-24 md:w-32 lg:w-18" />
          </Tooltip>
        )}
      </div>
    )
  }
)
CurationBadges.displayName = 'Product.CurationBadges'

const HeaderImageSelo = forwardRef<HTMLImageElement, THeaderImage & ImgHTMLAttributes<HTMLImageElement>>(
  (
    { className, isNationalProduct, isSupplementProduct, src, alt = 'product-img', product, shapeName, deliveryByAssociation, ...props },
    ref
  ) => {
    const { signedUser } = useSignedUser()
    const isPatientUser = useMemo(() => signedUser?.registration_type === 1, [signedUser])

    const defaultImage = isNationalProduct ? cannectDefaultProduct : cannectDefaultProductNoTarg
    const isPillProduct = product?.origin === 'pill'
    let imageSource = ''

    // Se for produto de associação obrigatória, usa imagem padrão da ASPAEC
    const isDeliveryByAssociation = deliveryByAssociation ?? product?.delivery_by_association
    if (isPillProduct) {
      imageSource = src || defaultImage
    } else if (isDeliveryByAssociation) {
      imageSource = aspaecDefaultProduct
    } else {
      const shapeNameFormatted = shapeName?.toLowerCase().replace(' ', '-')

      switch (shapeNameFormatted) {
        case 'óleo':
          imageSource = isPatientUser && !isSupplementProduct ? cannectDefaultProductOil : src || cannectDefaultProductOil
          break
        case 'cápsula':
          imageSource =
            isPatientUser && !isSupplementProduct ? cannectDefaultProductCapsule : src || cannectDefaultProductCapsule
          break
        case 'creme':
          imageSource =
            isPatientUser && !isSupplementProduct ? cannectDefaultProductCream : src || cannectDefaultProductCream
          break
        case 'goma':
          imageSource =
            isPatientUser && !isSupplementProduct ? cannectDefaultProductGummy : src || cannectDefaultProductGummy
          break
        default:
          imageSource = isPatientUser && !isSupplementProduct ? defaultImage : src || defaultImage
          break
      }
    }

    return (
      <div className="flex h-full w-full flex-col overflow-hidden rounded-2xl">
        <img
          src={imageSource}
          alt={alt}
          ref={ref}
          className={cn('h-full w-full object-cover shadow-md', className)}
          {...props}
        />
      </div>
    )
  }
)

HeaderImage.displayName = 'Product.HeaderImage'

const HeaderInfo = forwardRef<HTMLDivElement, THeaderInfo & HTMLAttributes<HTMLDivElement>>(
  ({ className, name, volume, unitOfMeasurement, brandName, ...props }, ref) => {
    const hasMedicalInfo = volume && unitOfMeasurement
    return (
      <div ref={ref} className={cn('flex flex-1 flex-col gap-1 overflow-hidden', className)} {...props}>
        <Typography.Text
          id="product-name"
          type="paragraphTwo"
          weight="semibold"
          className="break-words text-base font-semibold leading-tight text-neutral-900 sm:text-lg"
          ellipsis={{ rows: 3, tooltip: name }}>
          {name || ''}
        </Typography.Text>
        {hasMedicalInfo && (
          <Typography.Text type="captionOne" className="mb-1 text-sm text-neutral-600" id="product-volume">
            {volume || ''}
            {unitOfMeasurement?.toLocaleLowerCase() || ''}
          </Typography.Text>
        )}
        <Typography.Text type="captionOne" muted weight="semibold" className="text-sm text-neutral-500">
          {brandName || ''}
        </Typography.Text>
      </div>
    )
  }
)
HeaderInfo.displayName = 'Product.HeaderInfo'

const HeaderBadges = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('flex flex-wrap gap-1 sm:gap-2', className)} {...props} />
))

HeaderBadges.displayName = 'Product.HeaderBadges'

const Footer = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('mt-3 flex items-center justify-between sm:mt-4', className)} {...props} />
))
Footer.displayName = 'Product.Footer'

const Price = forwardRef<HTMLDivElement, TPrice & HTMLAttributes<HTMLDivElement>>(
  ({ className, currentPrice, promotionalPrice, textClassName, currencyClassName, ...props }, ref) => {
    if (!currentPrice) return null

    return (
      <div
        ref={ref}
        className={cn('flex min-w-fit flex-shrink-0 flex-col items-end whitespace-nowrap text-right', className)}
        {...props}>
        {promotionalPrice && (
          <Typography.Text type="captionOne" muted className="text-xs line-through sm:text-sm" weight="semibold">
            {formatToReal(currentPrice)}
          </Typography.Text>
        )}

        {!promotionalPrice && (
          <Typography.Text
            type="paragraphTwo"
            weight="semibold"
            id="product-price"
            className={cn('whitespace-nowrap text-sm sm:text-base', textClassName)}>
            <span className={cn('mr-1 text-xs sm:text-sm', currencyClassName)}>R$</span>
            {formatToReal(currentPrice).replace('R$\u00A0', '')}
          </Typography.Text>
        )}

        {promotionalPrice && (
          <Typography.Text
            type="paragraphTwo"
            weight="semibold"
            id="product-price"
            className={cn('whitespace-nowrap text-sm sm:text-base', textClassName)}>
            <span className={cn('mr-1 align-text-bottom text-xs sm:text-sm', currencyClassName)}>R$</span>
            {formatToReal(promotionalPrice).replace('R$\u00A0', '')}
          </Typography.Text>
        )}
      </div>
    )
  }
)

Price.displayName = 'Product.Price'

const RemoveButton = forwardRef<HTMLButtonElement, TRemoveButton>(
  ({ className, onRemove, shouldConfirmBeforeRemove = true, ...props }, ref) => {
    const modalRemoveConfirmRef = useRef<TModalConfirmMethods>(null)
    const handleClick = () => {
      if (shouldConfirmBeforeRemove) {
        modalRemoveConfirmRef.current?.onShow()
      } else {
        onRemove()
      }
    }
    return (
      <>
        <Button
          ref={ref}
          unstyled
          className={cn(
            'absolute right-0 top-0 text-base text-neutral-500 transition-all duration-300 ease-in-out hover:scale-105 sm:text-lg',
            className
          )}
          onClick={handleClick}
          {...props}>
          <LuTrash2 />
        </Button>
        <ModalConfirm
          ref={modalRemoveConfirmRef}
          title="Deseja realmente remover esse produto?"
          text="Ao confirmar, o produto será removido do carrinho."
          onConfirm={() => {
            onRemove()
            modalRemoveConfirmRef?.current?.onClose()
          }}
        />
      </>
    )
  }
)
RemoveButton.displayName = 'Product.RemoveButton'

const FreeShippingProgress = forwardRef<HTMLDivElement, TFreeShippingProgress & HTMLAttributes<HTMLDivElement>>(
  ({ summary, className, ...props }, ref) => {
    const FREE_SHIPPING_MINIMUM = 2500

    if (!summary) return null

    // amount_total are the products total amount without discount, to know the liquid amount we need to substract the discount
    const productsLiquidAmount = summary.amount_total - summary.discount_product

    const remainingForFreeShipping = Math.max(FREE_SHIPPING_MINIMUM - productsLiquidAmount, 0)
    const progress = Math.min((productsLiquidAmount / FREE_SHIPPING_MINIMUM) * 100, 100)
    const achievedGoal = remainingForFreeShipping === 0

    const contentVariants = {
      initial: { opacity: 0, y: 20 },
      animate: { opacity: 1, y: 0 },
      exit: { opacity: 0, y: -20 }
    }

    return (
      <div ref={ref} {...props} className={cn('w-full space-y-3 sm:space-y-4', className)}>
        <Typography.Text type="paragraphTwo" weight="semibold" className="text-sm sm:text-base">
          Custo de importação
        </Typography.Text>

        <ProgressBar
          value={progress}
          className="h-[8px] w-full sm:h-[10px]"
          indicatorClassName={achievedGoal ? 'bg-success-700' : ''}
        />

        <AnimatePresence mode="wait">
          {achievedGoal ? (
            <motion.div
              key="achieved"
              variants={contentVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.3 }}>
              <Typography.Text type="paragraphTwo" className="text-sm sm:text-base">
                <span className="font-semibold">Parabéns, você ganhou!</span>
                <br />
                Custo de importação <span className="font-semibold text-primary-700">grátis!</span>
              </Typography.Text>
            </motion.div>
          ) : (
            <motion.div
              key="not-achieved"
              variants={contentVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.3 }}>
              <Typography.Text type="paragraphTwo" className="text-sm sm:text-base">
                Faltam <span className="font-semibold">{formatToReal(Math.max(remainingForFreeShipping, 0))}</span> para{' '}
                <span className="font-semibold">Custo de importação grátis</span>
              </Typography.Text>
            </motion.div>
          )}
        </AnimatePresence>

        <Typography.Text
          type="captionOne"
          className={`text-xs sm:text-sm ${achievedGoal ? 'text-neutral-300' : ''} transition-all duration-500`}>
          Compre acima de <span className="font-semibold">R$ {FREE_SHIPPING_MINIMUM},00</span> e tenha custo de
          importação grátis em qualquer canal de pagamento disponível.
        </Typography.Text>
      </div>
    )
  }
)

FreeShippingProgress.displayName = 'Product.FreeShippingProgress'
const FlavorSelect = ({
  flavorsList,
  onChange,
  value,
  className,
  triggerText = 'Alterar sabor',
  isDisabled,
  ...triggerVariantsProps
}: TFlavorSelect) => {
  const isDesktop = useMediaQuery(' (min-width: 768px)')

  const { unstyled } = triggerVariantsProps

  const triggerVariants = buttonVariants({
    ...triggerVariantsProps,
    unstyled: unstyled ?? true,
    className: 'text-primary-700 text-xs sm:text-sm font-normal'
  })

  const RecommendedText = 'Recomendado'

  const closeRef = useRef<HTMLButtonElement>(null)

  const isOnlyOneFlavor = flavorsList?.length === 1

  const shouldDisableTrigger = isOnlyOneFlavor || isDisabled

  if (isDesktop)
    return (
      <Dropdown.Menu modal={false}>
        <Dropdown.MenuTrigger disabled={shouldDisableTrigger} className={cn(triggerVariants, className)}>
          {triggerText}
        </Dropdown.MenuTrigger>
        <Dropdown.MenuContent>
          {flavorsList
            .sort((a, b) => a.id - b.id)
            .map((flavor) => (
              <Dropdown.MenuItem
                className={`my my-2 flex h-[48px] items-center justify-between border-b sm:h-[52px] ${flavor?.name || (flavor?.flavor === value && 'bg-secondary-100')}`}
                onSelect={() => onChange(flavor.id)}>
                <div>
                  <Typography.Text muted weight="semibold" type="paragraphTwo" className="text-sm sm:text-base">
                    {flavor?.name || flavor?.flavor}
                  </Typography.Text>
                  {flavor?.isRecommendedFlavor && (
                    <div className="flex items-center gap-1 sm:gap-2">
                      <MdStars size={16} className="text-primary-700 sm:size-5" />
                      <Typography.Text type="captionOne" muted className="text-xs text-primary-700 sm:text-sm">
                        {RecommendedText}
                      </Typography.Text>
                    </div>
                  )}
                </div>
              </Dropdown.MenuItem>
            ))}
        </Dropdown.MenuContent>
      </Dropdown.Menu>
    )

  return (
    <Drawer.Root>
      <Drawer.Trigger disabled={shouldDisableTrigger} className={cn(triggerVariants, className)}>
        {triggerText}
      </Drawer.Trigger>
      <Drawer.Content className="w-full pb-4 sm:pb-6">
        <Drawer.Header className="pb-3 sm:pb-4">
          <Drawer.Title className="text-base sm:text-lg">Selecione o sabor</Drawer.Title>
        </Drawer.Header>
        <Separator />

        {flavorsList
          .sort((a, b) => a.id - b.id)
          .map((flavor) => (
            <div
              key={flavor.id}
              className={`relative flex h-[48px] w-full items-center border-0 border-b border-solid border-gray-300 py-2 sm:h-[52px] ${flavor?.name || (flavor?.flavor === value && 'bg-secondary-100')}`}>
              <Drawer.Close className="sr-only" ref={closeRef} />
              <Button
                unstyled
                full
                className="relative flex items-start justify-between px-3 sm:px-4"
                onClick={() => {
                  onChange(flavor.id)
                  closeRef.current?.click()
                }}>
                <div className="text-left">
                  <Typography.Text muted weight="semibold" type="paragraphTwo" className="text-sm sm:text-base">
                    {flavor?.name || flavor?.flavor}
                  </Typography.Text>
                </div>

                {flavor?.isRecommendedFlavor && (
                  <div className="flex min-w-fit items-center gap-1 sm:gap-2">
                    <MdStars size={16} className="text-primary sm:size-5" />
                    <Typography.Text type="captionOne" muted className="text-xs text-primary-700 sm:text-sm">
                      {RecommendedText}
                    </Typography.Text>
                  </div>
                )}
              </Button>
            </div>
          ))}
      </Drawer.Content>
    </Drawer.Root>
  )
}

export {
  CurationBadges,
  FlavorSelect,
  Footer,
  FreeShippingProgress,
  Header,
  HeaderBadges,
  HeaderImage,
  HeaderImageSelo,
  HeaderInfo,
  Price,
  RemoveButton,
  Root
}
